// #3190 (station-monitor workstream) -- pure helpers for the Station Monitor
// page and the dashboard strip, unit tested independently of React.
//
// The browser no longer joins presence with snapshots (that join, and the
// "Unnamed Scribe n" / station number naming that goes with it, moved to
// score-api's GET /show/:showId/station-monitor in #3188/#3189). One server
// row per station:
//   { stationName|null, stationIdSuffix, isTest, deviceId, lastSeenAt, live, shipped, queued,
//     needsAttention, oldestUnshippedAt, snapshots: [...], segments: [...] }
// deviceId and each snapshot's scribeSub are kept ONLY because the reconcile
// and mark-reviewed calls are keyed by them. Nothing here puts either on
// screen.

import { DEFAULT_STUCK_STATION_THRESHOLD_MS, formatRing, isStationStuck, unshippedAgeMs } from "score-common/stuck-station";
import { SILENT_STATION_THRESHOLD_MS, isStationSilent, silentForMs } from "score-common/silent-station";
import { parseTimestampMs } from "./scribePresence";

// #3436: a station is flagged when its device has used at least this share of
// the browser's storage quota. One named constant, tune it here.
export const DEVICE_STORAGE_NEARLY_FULL_RATIO = 0.8;

export const DEVICE_HEALTH_ISSUES = Object.freeze({
  storageNotPersistent: "storageNotPersistent",
  storageNearlyFull: "storageNearlyFull",
  storageFull: "storageFull",
  authExpired: "authExpired",
  syncStuck: "syncStuck",
  offline: "offline",
});

// The device health a station's freshest heartbeat reported (score-api's
// `health`), reduced to what staff should act on. Returns:
//   { known: false, issues: [] }      no health (an older scribe-fe build, or no
//                                     heartbeat): shown as a dash, never flagged
//   { known: true, issues: [...] }    issues is empty for a healthy device; each
//                                     entry is { code, count?, percent? }
// Reusable on its own (the dashboard strip and the scribe chips read the same
// rules), so it stays pure and returns codes, never display text.
export function summarizeDeviceHealth(health) {
  if (!health || typeof health !== "object") return { known: false, issues: [] };
  const issues = [];
  if (health.storagePersisted === false) issues.push({ code: DEVICE_HEALTH_ISSUES.storageNotPersistent });
  if (health.storageFull === true) {
    issues.push({ code: DEVICE_HEALTH_ISSUES.storageFull });
  } else if (
    typeof health.storageUsage === "number" &&
    typeof health.storageQuota === "number" &&
    health.storageQuota > 0 &&
    health.storageUsage / health.storageQuota >= DEVICE_STORAGE_NEARLY_FULL_RATIO
  ) {
    issues.push({
      code: DEVICE_HEALTH_ISSUES.storageNearlyFull,
      percent: Math.round((health.storageUsage / health.storageQuota) * 100),
    });
  }
  if (health.authExpired === true) issues.push({ code: DEVICE_HEALTH_ISSUES.authExpired });
  if (health.syncStuck > 0) issues.push({ code: DEVICE_HEALTH_ISSUES.syncStuck, count: health.syncStuck });
  if (health.online === false) issues.push({ code: DEVICE_HEALTH_ISSUES.offline });
  return { known: true, issues };
}

export function snapshotKey(deviceId, scribeSub) {
  return `${deviceId ?? ""}::${scribeSub ?? ""}`;
}

// Chronological min by parsed milliseconds (not by string: a value can be ISO
// or score-api's `<epoch ms>-<hex>` stamp, #3239). Unreadable values are
// skipped, so the strip can always format what it returns.
function earliestTimestamp(timestamps) {
  const real = timestamps.filter((t) => parseTimestampMs(t) !== null);
  if (!real.length) return null;
  return real.reduce((oldest, t) => (parseTimestampMs(t) < parseTimestampMs(oldest) ? t : oldest));
}

// A station's lastSeenAt as an ISO string, or null when unreadable (#3239).
function normalizeSeenAt(value) {
  const ms = parseTimestampMs(value);
  return ms === null ? null : new Date(ms).toISOString();
}

// #3507: the Station Monitor shows only stations in contact within this window
// by default. A wiped or retired tablet comes back as a NEW row (the row is one
// browser id), so old rows pile up for the whole show and read as an outage.
export const STALE_STATION_MS = 2 * 60 * 60 * 1000;

// Does this station still hold work that someone must act on? The hard rule of
// #3507: such a row is NEVER hidden for being old. Only an admin's Clear station
// (the server's `cleared`) retires it, so it stays alarming until a human has
// checked it.
export function stationHoldsWork(row) {
  return (
    row.watchSilence === true ||
    (row.ringMovedOn ?? []).length > 0 ||
    (row.needsAttention ?? 0) > 0 ||
    (row.queued ?? 0) > 0
  );
}

// Hidden from the default view, the banners and the dashboard strip: a station
// an admin cleared (and that has not made contact since, the server decides
// that), one the server RETIRED because its Reset tablet is done and it holds no work
// (#3654: the tablet is a new station now, the server decides), or one with no contact
// for STALE_STATION_MS that holds no work.
export function isStationHiddenByDefault(row, now) {
  if (row.cleared === true || row.retired === true) return true;
  if (stationHoldsWork(row)) return false;
  if (row.live === true) return false;
  const seen = parseTimestampMs(row.lastSeenAt);
  return seen === null || now - seen > STALE_STATION_MS;
}

// { visible, older } in the server's order. `visible` is what the default view,
// every banner and the strip's counts are built from.
export function splitStationRows(rows, now = Date.now()) {
  const visible = [];
  const older = [];
  for (const row of rows ?? []) (isStationHiddenByDefault(row, now) ? older : visible).push(row);
  return { visible, older };
}

// Show-wide worst-case numbers for the dashboard strip, built from the SAME
// server rows the monitor page renders, so the strip and the page can never
// disagree about a count. `queuedTotal` is exposed so the strip refuses to say
// "clean" while anything is still queued (#2827 judge fix r1). A station that
// is live but has no snapshot at all is "never uploaded" (#2827 AC4); a
// station that is not live and has no snapshot is just a station that was
// here, not one the tent is waiting on.
export function summarizeStationMonitorRows(rows) {
  const neverUploadedCount = rows.filter((r) => r.live && (r.snapshots ?? []).length === 0).length;
  const needsAttentionTotal = rows.reduce((sum, r) => sum + (r.needsAttention ?? 0), 0);
  const queuedTotal = rows.reduce((sum, r) => sum + (r.queued ?? 0), 0);
  const oldestUnshippedAt = earliestTimestamp(rows.map((r) => r.oldestUnshippedAt));
  const anyConnected = rows.some((r) => r.live);
  // #3447: tablets still running an old scribe-fe build while connected. A quiet
  // station on an old build is not one staff can usefully reload, so it is not counted.
  const oldBuildCount = rows.filter((r) => r.live && r.oldBuild === true).length;
  return { neverUploadedCount, needsAttentionTotal, queuedTotal, oldestUnshippedAt, anyConnected, oldBuildCount };
}

// A segment's end as the operator should read it. The server leaves an open
// segment's `to` null for as long as no later segment closes it, which stays
// true after a station goes quiet (the presence TTL is 1h), so only a LIVE
// station's open segment is still working. A quiet station's open segment
// ended when the station was last seen.
export function segmentEnd(segment, station) {
  if (segment.to) return { at: segment.to, ongoing: false };
  if (station.live) return { at: null, ongoing: true };
  return { at: station.lastSeenAt ?? null, ongoing: false };
}

// #3508: a station is its NAME (given once at setup) plus the last 4 characters
// of its id, which tell two same-name rows apart. The per-show "Station N" is
// gone. Ordered by name, unnamed stations last, then by id so the order is stable.
const hasName = (s) => typeof s.stationName === "string" && s.stationName !== "";
const idTail = (s) => (typeof s.stationIdSuffix === "string" ? s.stationIdSuffix : String(s.deviceId ?? "").slice(-4));

function compareStations(a, b) {
  if (hasName(a) !== hasName(b)) return hasName(a) ? -1 : 1;
  if (hasName(a)) {
    const byName = a.stationName.localeCompare(b.stationName, "en", { sensitivity: "base", numeric: true });
    if (byName !== 0) return byName;
  }
  return String(a.deviceId) < String(b.deviceId) ? -1 : String(a.deviceId) > String(b.deviceId) ? 1 : 0;
}

/** How a station reads in a banner line: `pine-frog (\u20267f3a)`, or "" for a station with no name yet. */
export function stationLabel(s) {
  return hasName(s) ? `${s.stationName} (\u2026${idTail(s)})` : "";
}

function emptyRow(station, extra) {
  return {
    key: snapshotKey(station.deviceId, null),
    deviceId: station.deviceId,
    scribeSub: null,
    scribeName: null,
    ring: null,
    position: null,
    from: null,
    end: null,
    snapshot: null,
    snapshotSeenElsewhere: false,
    ...extra,
  };
}

// Shapes server rows into render groups: one group per station (by station name), one table row per work
// segment (a scribe who moved shows one row per stretch of work). Counts and
// reconcile state belong to a scribe's snapshot, which a scribe has once per
// station however many segments they worked, so it rides on that scribe's
// FIRST segment row; later segments of the same scribe leave those cells blank
// (`carriesState` false). A snapshot whose scribe has no segment still gets a
// row (no ring/position).
export function buildStationGroups(stations) {
  return [...stations].sort(compareStations).map((rawStation) => {
    const station = { ...rawStation, lastSeenAt: normalizeSeenAt(rawStation.lastSeenAt) };
    const snapshots = station.snapshots ?? [];
    const snapshotsBySub = new Map(snapshots.map((s) => [s.scribeSub, s]));
    const claimed = new Set();
    const rows = [];

    (station.segments ?? []).forEach((segment, i) => {
      const sub = segment.scribeSub ?? null;
      const firstOfScribe = !claimed.has(sub);
      claimed.add(sub);
      rows.push(
        emptyRow(station, {
          rowKey: `${station.deviceId}::seg${i}`,
          key: snapshotKey(station.deviceId, sub),
          scribeSub: sub,
          scribeName: segment.scribeName ?? null,
          ring: segment.ring ?? null,
          position: segment.position ?? null,
          from: segment.from ?? null,
          end: segmentEnd(segment, station),
          carriesState: firstOfScribe,
          snapshot: firstOfScribe && sub !== null ? snapshotsBySub.get(sub) ?? null : null,
        }),
      );
    });

    for (const snapshot of snapshots) {
      if (claimed.has(snapshot.scribeSub)) continue;
      claimed.add(snapshot.scribeSub);
      rows.push(
        emptyRow(station, {
          rowKey: `${station.deviceId}::snap${snapshot.scribeSub}`,
          key: snapshotKey(station.deviceId, snapshot.scribeSub),
          scribeSub: snapshot.scribeSub,
          scribeName: snapshot.scribeName ?? null,
          carriesState: true,
          snapshot,
        }),
      );
    }

    if (rows.length === 0) {
      rows.push(emptyRow(station, { rowKey: `${station.deviceId}::empty`, carriesState: true }));
    }

    for (const row of rows) row.snapshotSeenElsewhere = row.snapshot === null && snapshots.length > 0;

    return {
      key: station.deviceId,
      stationName: hasName(station) ? station.stationName : null,
      stationIdSuffix: idTail(station),
      isTest: station.isTest === true,
      lastSeenAt: station.lastSeenAt,
      // #3507: the server's clear verdict, never recomputed here.
      cleared: station.cleared === true,
      // #3654: the server's verdict that this tablet's reset is done and the row is dead.
      retired: station.retired === true,
      clearedAt: station.clearedAt ?? null,
      clearedByName: station.clearedByName ?? null,
      live: Boolean(station.live),
      // #3518: the server's Clear The Machine state (pending | blocked | done), or null.
      machineClear: station.machineClear ?? null,
      // #3447: the server's verdict, never recomputed here.
      appBuild: station.appBuild ?? null,
      liveBuild: station.liveBuild ?? null,
      oldBuild: station.oldBuild === true,
      // #3436: the server's health object, reduced by summarizeDeviceHealth.
      deviceHealth: summarizeDeviceHealth(station.health),
      rows,
    };
  });
}

// #3427: the stations whose oldest unshipped op has waited longer than the
// threshold (default 3 minutes, DEFAULT_STUCK_STATION_THRESHOLD_MS). Reads the
// SAME server rows as everything else here; a row's oldestUnshippedAt is already
// scoped to the selected show by score-api (#3433), so another show's old ops on
// the tablet never make a station stuck. The classifier itself is shared with
// score-api's once-per-breach alert so the banner and the chat alert agree.
// Ring is the station's most recent segment's ring. Sorted by station name,
// unnumbered last, longest-waiting first within ties.
export function findStuckStations(rows, now, thresholdMs = DEFAULT_STUCK_STATION_THRESHOLD_MS) {
  return (rows ?? [])
    .filter((r) => isStationStuck(r.oldestUnshippedAt, now, thresholdMs))
    .map((r) => {
      const segments = r.segments ?? [];
      return {
        key: r.deviceId,
        stationName: hasName(r) ? r.stationName : null,
        stationLabel: stationLabel(r),
        ring: formatRing(segments.length ? segments[segments.length - 1].ring : null),
        ageMs: unshippedAgeMs(r.oldestUnshippedAt, now),
        // #3525: a tablet that is calling in gets different advice from one that is not.
        live: r.live === true,
      };
    })
    .sort((a, b) => compareStations(a, b) || b.ageMs - a.ageMs);
}

// #3475: the stations that have gone silent on a running ring. score-api decides
// the ring half (`watchSilence`: rides due now, last contact today, not replaced
// on that ring and seat, see lib/ring-running.js) and the shared classifier
// decides the quiet half (no contact for 2 minutes), so a tablet asleep between
// classes or after the day never shows here. `unsent` is the unsent-sheet count
// from the station's last heartbeat (#3436 device health), null from an older
// build that does not send it. A station that heartbeats again moves its
// lastSeenAt forward on the next read and drops out. Sorted by station name.
export function findSilentStations(rows, now, thresholdMs = SILENT_STATION_THRESHOLD_MS) {
  return (rows ?? [])
    .filter((r) => r.watchSilence === true && isStationSilent(r.lastSeenAt, now, thresholdMs))
    .map((r) => {
      const segments = r.segments ?? [];
      const unsent = r.health?.unsyncedRides;
      return {
        key: r.deviceId,
        stationName: hasName(r) ? r.stationName : null,
        stationLabel: stationLabel(r),
        ring: formatRing(segments.length ? segments[segments.length - 1].ring : null),
        silentMs: silentForMs(r.lastSeenAt, now),
        unsent: Number.isInteger(unsent) && unsent >= 0 ? unsent : null,
      };
    })
    .sort(compareStations);
}

// #3476: the started sheets a ring has moved past. score-api decides everything
// (a sheet with marks that nobody signed, no term reason, a later ride in the
// same ring with marks, see score-api lib/ring-moved-on.js) and names the station
// that holds it, so this only flattens the per-station `ringMovedOn` lists into
// one line per ride and seat. It clears on the first read after the sheet is
// signed or the ride gets a term reason. Sorted by station name, then ring and
// ride number.
export function findRingMovedOnSheets(rows) {
  const lines = [];
  for (const r of rows ?? []) {
    for (const item of r.ringMovedOn ?? []) {
      lines.push({
        key: `${r.deviceId}::${item.rideId}::${item.seat ?? ""}`,
        deviceId: r.deviceId,
        stationName: hasName(r) ? r.stationName : null,
        stationLabel: stationLabel(r),
        ring: formatRing(item.ring),
        rideNumber: Number.isInteger(item.rideNumber) ? item.rideNumber : null,
        riderName: item.riderName || null,
        seat: item.seat || null,
        station: r,
      });
    }
  }
  return lines
    .sort(
      (a, b) =>
        compareStations(a.station, b.station) ||
        String(a.ring).localeCompare(String(b.ring), "en", { numeric: true }) ||
        (a.rideNumber ?? Infinity) - (b.rideNumber ?? Infinity) ||
        String(a.seat).localeCompare(String(b.seat))
    )
    .map((line) => {
      const out = { ...line };
      delete out.station;
      return out;
    });
}
